import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { TriggeredHook } from "sonobello.utilities.react";
import { LeafLogoSvg, SBPalette } from "sonobello.utilities.react.mui";

import theme from "../../../../assets/Theme";
import EnvironmentConfiguration from "../../../../constants/EnvironmentConfiguration";
import { LeadResult } from "../../../../dtos/LeadResult";
import { Step } from "../../../../types/Step";
import { isMobileView } from "../../../../utils/Constants";
import { IFlowStepProps } from "../../../Types/IFlow";
import StepWrapper, { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import IMedicalQualification from "../Types/IMedicalQualification";
import IMedicalQuestionnaireCompleteEvaluator from "../Types/IMedicalQualificationCompleteEvaluator";
import IMedicalQualificationNonCandidateEvaluator from "../Types/IMedicalQualificationNonCandidateEvaluator";
import { IMedicalStepProps } from "./FormSeriesMedicalStep";

// Question IDs — must match the keys used by the existing evaluator and form state.
const Q_DIALYSIS = "generalMedicalQuestions.dialysis";
const Q_OXYGEN = "generalMedicalQuestions.oxygen";
const Q_ORGAN_TRANSPLANT = "generalMedicalQuestions.organTransplant";
const Q_BLEEDING_DISORDER = "generalMedicalQuestions.bleedingDisorder";
const Q_BLOOD_CLOT = "generalMedicalQuestions.bloodClot";
const Q_PACEMAKER = "pacemakerMedicalQuestions.pacemaker";
const Q_DEFIBRILLATOR = "pacemakerMedicalQuestions.defibrillator";
const Q_CANCER_TREATMENT = "radiationMedicalQuestions.cancer";
const Q_ACTIVE_CANCER = "radiationMedicalQuestions.chemo";
const Q_CANCER_CHEMO = "radiationMedicalQuestions.cancerChemo";
const Q_DIABETIC = "diabeticMedicalQuestions.diabetic";

/** The configuration for the checkbox opt-in medical step. Questions are defined internally. */
export interface ICheckboxOptInMedicalStepConfig {
  /** Evaluates the full form for completeness and non-candidacy. */
  medicalQualificationEvaluator: IMedicalQualificationNonCandidateEvaluator & IMedicalQuestionnaireCompleteEvaluator;
  /** The step to navigate to after the user submits and is not a non-candidate. */
  nextStep: Step;
  /** A hook to be called when the user is disqualified as a non-candidate. */
  useCreateNonCandidateLeadResult: TriggeredHook<LeadResult, undefined, boolean>;
}

// ─── sub-components ────────────────────────────────────────────────────────────

interface ICheckboxQuestionRowProps {
  questionId: string;
  text: string;
  /** "yes" checkbox: checking = true, unchecking = null. "no" checkbox: checking = false, unchecking = null. */
  checkboxType: "yes" | "no";
  value: IMedicalQualification;
  onChange: (id: string, val: boolean | null) => void;
  isMobile: boolean;
}

/** A single question row rendered with a labelled Yes or No checkbox instead of a Yes/No toggle. */
const CheckboxQuestionRow: React.FC<ICheckboxQuestionRowProps> = ({
  questionId,
  text,
  checkboxType,
  value,
  onChange,
  isMobile
}) => {
  const rawValue = value[questionId] ?? null;
  const isChecked = checkboxType === "yes" ? rawValue === true : rawValue === false;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      onChange(questionId, checkboxType === "yes" ? true : false);
    } else {
      onChange(questionId, null);
    }
  };

  return (
    <ListItem sx={{ px: isMobile ? 0 : undefined, display: "flex", justifyContent: "space-between" }}>
      <ListItemIcon sx={{ minWidth: isMobile ? theme.spacing(4) : undefined }}>
        <LeafLogoSvg
          fill={SBPalette.dark.watermarkGray.main}
          style={{ height: theme.spacing(3), width: theme.spacing(3) }}
        />
      </ListItemIcon>
      <ListItemText primary={text} sx={{ pr: 2 }} />
      <FormControlLabel
        control={<Checkbox checked={isChecked} onChange={handleChange} size="small" color="primary" />}
        label={
          <Typography
            variant="caption"
            color={isChecked ? "primary.main" : "text.secondary"}
            fontWeight={isChecked ? 600 : 400}
          >
            {checkboxType === "yes" ? "Yes" : "No"}
          </Typography>
        }
        labelPlacement="end"
        sx={{
          border: 1,
          borderColor: isChecked ? "primary.main" : "divider",
          borderRadius: 1,
          pl: 0.5,
          pr: 1,
          m: 0,
          flexShrink: 0,
          "&:hover": { borderColor: "primary.main" }
        }}
      />
    </ListItem>
  );
};

interface IInfoNoteRowProps {
  text: string;
  isMobile: boolean;
}

/** An informational message row with no input element, matching the visual style of question rows. */
const InfoNoteRow: React.FC<IInfoNoteRowProps> = ({ text, isMobile }) => (
  <ListItem sx={{ px: isMobile ? 0 : undefined }}>
    <ListItemIcon sx={{ minWidth: isMobile ? theme.spacing(4) : undefined }}>
      <LeafLogoSvg
        fill={SBPalette.dark.watermarkGray.main}
        style={{ height: theme.spacing(3), width: theme.spacing(3) }}
      />
    </ListItemIcon>
    <ListItemText primary={text} primaryTypographyProps={{ variant: "body2", color: "text.secondary" }} />
  </ListItem>
);

// ─── main step component ────────────────────────────────────────────────────────

/** Renders all medical questions on a single scrollable page. Every question uses a single **Yes** checkbox.
 * Checking Yes reveals child content for questions that have sub-questions or informational notes.
 * Unchecking or leaving blank is resolved to No at submit.
 *
 * The Next button is enabled once the user has scrolled to the bottom of the form (latched).
 * Any question left unchecked at submit is treated as No.
 */
const CheckboxOptInMedicalStep: React.FC<
  IMedicalStepProps & ICheckboxOptInMedicalStepConfig & IStepWrapperButtonProps
> = ({
  medicalQualificationEvaluator,
  nextStep,
  router,
  value,
  onMedicalNoncandidateDisqualification,
  setMedicalConclusions,
  setMedicalFormState,
  useCreateNonCandidateLeadResult,
  NextButtonMobile,
  NextButtonDesktop
}) => {
  const isMobile = isMobileView();
  const flowStepProps = useOutletContext<IFlowStepProps>();
  const { execute: createNonCandidateLeadResult, result, isLoading } = useCreateNonCandidateLeadResult();

  // One-way latch: true once the user has scrolled to the bottom.
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // One-way scroll-to-bottom latch. Also fires immediately on mount to handle pages that fit in the viewport.
  useEffect(() => {
    if (hasScrolledToBottom) return;

    const checkBottom = () => {
      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 20) {
        setHasScrolledToBottom(true);
      }
    };

    checkBottom();
    window.addEventListener("scroll", checkBottom, { passive: true });
    return () => window.removeEventListener("scroll", checkBottom);
  }, [hasScrolledToBottom]);

  useEffect(() => {
    if (result) router.goToNonCandidateView();
  }, [result]);

  // Toggle a single question value, cascading resets to dependent child questions.
  const handleChange = (questionId: string, val: boolean | null) => {
    setMedicalFormState(prev => {
      const next = { ...prev, [questionId]: val };
      if (questionId === Q_PACEMAKER) next[Q_DEFIBRILLATOR] = null;
      if (questionId === Q_CANCER_TREATMENT) {
        next[Q_ACTIVE_CANCER] = null;
        next[Q_CANCER_CHEMO] = null;
      }
      if (questionId === Q_ACTIVE_CANCER) next[Q_CANCER_CHEMO] = null;
      return next;
    });
  };

  // Determine which question IDs are currently visible (not hidden inside a collapsed section).
  const computeVisibleQuestions = (): Set<string> => {
    const visible = new Set<string>([
      Q_DIALYSIS,
      Q_OXYGEN,
      Q_ORGAN_TRANSPLANT,
      Q_BLEEDING_DISORDER,
      Q_BLOOD_CLOT,
      Q_PACEMAKER,
      Q_CANCER_TREATMENT,
      Q_DIABETIC
    ]);

    if (value[Q_PACEMAKER] === true) visible.add(Q_DEFIBRILLATOR);

    if (value[Q_CANCER_TREATMENT] === true) {
      visible.add(Q_ACTIVE_CANCER);
      if (value[Q_ACTIVE_CANCER] === false) visible.add(Q_CANCER_CHEMO);
    }

    return visible;
  };

  // Apply null → inverse resolution for all visible questions before evaluation.
  const resolveNullAnswers = (form: IMedicalQualification, visible: Set<string>): IMedicalQualification => {
    const resolved = { ...form };

    // Yes checkboxes: unchecked (null) → No (false)
    [
      Q_DIALYSIS,
      Q_OXYGEN,
      Q_ORGAN_TRANSPLANT,
      Q_BLEEDING_DISORDER,
      Q_BLOOD_CLOT,
      Q_PACEMAKER,
      Q_CANCER_TREATMENT,
      Q_CANCER_CHEMO,
      Q_DIABETIC
    ].forEach(id => {
      if (resolved[id] == null && visible.has(id)) resolved[id] = false;
    });

    // No checkboxes: unchecked (null) → Yes (true)
    [Q_DEFIBRILLATOR, Q_ACTIVE_CANCER].forEach(id => {
      if (resolved[id] == null && visible.has(id)) resolved[id] = true;
    });

    return resolved;
  };

  const handleNext = () => {
    const visible = computeVisibleQuestions();
    const resolved = resolveNullAnswers(value, visible);

    setMedicalFormState(resolved);
    setMedicalConclusions({
      hasPacemakerWithoutDefibrillator: resolved[Q_DEFIBRILLATOR] === false,
      hasRadiationProtectedAreas: resolved[Q_CANCER_TREATMENT] === true,
      hasDiabetes: resolved[Q_DIABETIC] === true
    });

    if (medicalQualificationEvaluator.isNonCandidate(resolved)) {
      onMedicalNoncandidateDisqualification();
      createNonCandidateLeadResult();
    } else {
      flowStepProps.setStep(nextStep);
    }
  };

  const isPacemakerExpanded = value[Q_PACEMAKER] === true;
  const isCancerExpanded = value[Q_CANCER_TREATMENT] === true;
  const isDiabeticExpanded = value[Q_DIABETIC] === true;

  const commonProps = { value, onChange: handleChange, isMobile };

  return (
    <StepWrapper
      NextButtonMobile={NextButtonMobile}
      NextButtonDesktop={NextButtonDesktop}
      nextButtonLoading={isLoading}
      nextButtonLabel="Submit"
      onNext={handleNext}
      disabled={isLoading || !hasScrolledToBottom}
    >
      <Box display="flex" justifyContent="center" width="100%">
        <Box sx={{ width: "100%", maxWidth: 600, pb: "72px" }}>
          <List disablePadding>
            {/* ── General questions — Yes checkboxes ─────────────────────── */}
            <CheckboxQuestionRow
              {...commonProps}
              questionId={Q_DIALYSIS}
              text="Are you on Dialysis?"
              checkboxType="yes"
            />
            <CheckboxQuestionRow
              {...commonProps}
              questionId={Q_OXYGEN}
              text="Do you use an oxygen tank while awake?"
              checkboxType="yes"
            />
            <CheckboxQuestionRow
              {...commonProps}
              questionId={Q_ORGAN_TRANSPLANT}
              text="Have you had an organ transplant or are you currently on the organ transplant waiting list?"
              checkboxType="yes"
            />
            <CheckboxQuestionRow
              {...commonProps}
              questionId={Q_BLEEDING_DISORDER}
              text="Do you have a bleeding disorder?"
              checkboxType="yes"
            />
            <CheckboxQuestionRow
              {...commonProps}
              questionId={Q_BLOOD_CLOT}
              text="Have you had a blood clot in the last 12 months?"
              checkboxType="yes"
            />

            {/* ── Pacemaker — Yes reveals defibrillator sub-question ─────────── */}
            <CheckboxQuestionRow
              {...commonProps}
              questionId={Q_PACEMAKER}
              text="Do you have a pacemaker?"
              checkboxType="yes"
            />
            <Collapse in={isPacemakerExpanded}>
              <List disablePadding sx={{ pl: 2 }}>
                <CheckboxQuestionRow
                  {...commonProps}
                  questionId={Q_DEFIBRILLATOR}
                  text="Does it have a defibrillator?"
                  checkboxType="no"
                />
                <Collapse in={value[Q_DEFIBRILLATOR] === false}>
                  <InfoNoteRow
                    isMobile={isMobile}
                    text="Thank you for sharing. During your consultation, we will review your medical history in greater detail as this could impact your eligibility for surgery."
                  />
                </Collapse>
              </List>
            </Collapse>

            {/* ── Cancer — Yes reveals radiation info + sub-questions ───────── */}
            <CheckboxQuestionRow
              {...commonProps}
              questionId={Q_CANCER_TREATMENT}
              text="Have you been treated for cancer?"
              checkboxType="yes"
            />
            <Collapse in={isCancerExpanded}>
              <List disablePadding sx={{ pl: 2 }}>
                <InfoNoteRow
                  isMobile={isMobile}
                  text="Thank you for sharing. We are unable to work on any areas of the body previously treated with radiation."
                />
                <CheckboxQuestionRow
                  {...commonProps}
                  questionId={Q_ACTIVE_CANCER}
                  text="Do you have active cancer?"
                  checkboxType="no"
                />
                <Collapse in={value[Q_ACTIVE_CANCER] === false}>
                  <CheckboxQuestionRow
                    {...commonProps}
                    questionId={Q_CANCER_CHEMO}
                    text="Are you undergoing chemotherapy treatments?"
                    checkboxType="yes"
                  />
                </Collapse>
              </List>
            </Collapse>

            {/* ── Diabetes — Yes reveals A1C info note ───────────────────── */}
            <CheckboxQuestionRow
              {...commonProps}
              questionId={Q_DIABETIC}
              text="Do you have diabetes?"
              checkboxType="yes"
            />
            <Collapse in={isDiabeticExpanded}>
              <List disablePadding sx={{ pl: 2 }}>
                <InfoNoteRow
                  isMobile={isMobile}
                  text={`Thank you. An A1C of ${EnvironmentConfiguration.a1cUpperLimit} or below is required for surgery. Please bring your most recent A1C reading to review during your consult.`}
                />
              </List>
            </Collapse>
          </List>
        </Box>
      </Box>
    </StepWrapper>
  );
};

export default CheckboxOptInMedicalStep;
